import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import EmojiPicker, { Categories } from "emoji-picker-react";

// Skip the "Suggested" category so no empty/skeleton row is shown before
// the user has picked any emoji yet.
const EMOJI_CATEGORIES = [
  { category: Categories.SMILEYS_PEOPLE },
  { category: Categories.ANIMALS_NATURE },
  { category: Categories.FOOD_DRINK },
  { category: Categories.TRAVEL_PLACES },
  { category: Categories.ACTIVITIES },
  { category: Categories.OBJECTS },
  { category: Categories.SYMBOLS },
  { category: Categories.FLAGS },
];

const PICKER_WIDTH = 300;
const PICKER_HEIGHT = 350;
const VIEWPORT_MARGIN = 8;

/*
Reusable emoji picker trigger + popover.
Used by the quick top-level comment bar, the nested reply bar and both
reply modals so emoji support behaves identically everywhere a comment
or reply can be composed.

The popover is portaled to document.body and positioned with `fixed`
coordinates computed from the trigger's own bounding box, rather than
being absolutely positioned inside its normal DOM parent. Composers that
open inside a Bootstrap modal (limited height, its own overflow/scroll
box) would otherwise clip or cut off a popover trying to render above/
below the trigger within that constrained box.
*/
const EmojiPickerButton = ({ onSelect, disabled = false, className = "" }) => {
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState(null);
  const [portalTarget, setPortalTarget] = useState(null);
  const triggerRef = useRef(null);
  const popoverRef = useRef(null);

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (
        triggerRef.current &&
        !triggerRef.current.contains(event.target) &&
        popoverRef.current &&
        !popoverRef.current.contains(event.target)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, []);

  useLayoutEffect(() => {
    if (!open || !triggerRef.current) return;

    // Portal into the nearest Bootstrap modal root (if the trigger lives
    // inside one) rather than always document.body, so we stay within
    // the modal's own focus trap while still escaping its Modal.Body
    // overflow clipping via fixed positioning.
    setPortalTarget(triggerRef.current.closest(".modal") || document.body);

    const computePosition = () => {
      const rect = triggerRef.current.getBoundingClientRect();
      const spaceAbove = rect.top;
      const spaceBelow = window.innerHeight - rect.bottom;
      const openUpward = spaceAbove > PICKER_HEIGHT + VIEWPORT_MARGIN || spaceAbove > spaceBelow;
      let left = rect.left;
      if (left + PICKER_WIDTH > window.innerWidth - VIEWPORT_MARGIN) {
        left = Math.max(VIEWPORT_MARGIN, window.innerWidth - PICKER_WIDTH - VIEWPORT_MARGIN);
      }
      const top = openUpward
        ? Math.max(VIEWPORT_MARGIN, rect.top - PICKER_HEIGHT - VIEWPORT_MARGIN)
        : Math.min(rect.bottom + VIEWPORT_MARGIN, window.innerHeight - PICKER_HEIGHT - VIEWPORT_MARGIN);
      setPosition({ top, left });
    };

    computePosition();
    window.addEventListener("resize", computePosition);
    window.addEventListener("scroll", computePosition, true);
    return () => {
      window.removeEventListener("resize", computePosition);
      window.removeEventListener("scroll", computePosition, true);
    };
  }, [open]);

  return (
    <div className={`emoji-picker-wrapper d-inline-block ${className}`} ref={triggerRef}>
      <i
        className="material-symbols-outlined filled me-3 cursor-pointer"
        onMouseDown={(e) => e.preventDefault()}
        onClick={() => !disabled && setOpen(!open)}
        style={disabled ? { pointerEvents: "none", opacity: 0.4 } : {}}
      >
        mood
      </i>
      {open &&
        position &&
        portalTarget &&
        ReactDOM.createPortal(
          <div
            ref={popoverRef}
            className="emoji-picker-popover"
            style={{ position: "fixed", top: position.top, left: position.left, zIndex: 1060 }}
          >
            <EmojiPicker
              onEmojiClick={(emojiData) => {
                onSelect(emojiData.emoji);
                setOpen(false);
              }}
              autoFocusSearch={false}
              lazyLoadEmojis={false}
              categories={EMOJI_CATEGORIES}
              height={PICKER_HEIGHT}
              width={PICKER_WIDTH}
            />
          </div>,
          portalTarget
        )}
    </div>
  );
};
export default EmojiPickerButton;
